import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A dialog for the edit forms.
 *
 * Uses the native `<dialog>` element, which brings focus trapping, Escape to
 * close, inert background content and the top layer for free — all things a
 * hand-rolled div gets wrong, usually by omitting them.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}): React.JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * Whether the gesture that is finishing *began* on the backdrop.
   *
   * A `<dialog>` reports backdrop clicks as clicks on itself, so the obvious
   * test is `event.target === dialog`. That test is wrong, and the bug it caused
   * was reported from every edit dialog in the app: select text inside a field,
   * drag past the edge of the dialog, release — the click event is then
   * dispatched on the nearest common ancestor of press and release, which is the
   * dialog, and the form closed with the edit thrown away.
   *
   * Requiring the press to have landed on the backdrop too fixes it, and also
   * fixes the reverse gesture: pressing on the backdrop and releasing inside no
   * longer closes.
   */
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Escape fires `cancel`, not `close`, and the default would close the
    // dialog without telling React — leaving the two out of step.
    const onCancel = (event: Event): void => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [onClose]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      aria-label={title}
      /*
       * `m-auto` is doing real work. A modal `<dialog>` centres itself through
       * `margin: auto`, and Tailwind's preflight sets `margin: 0` on every
       * element — so every edit dialog in this app has been opening pinned to
       * the top-left corner of the viewport.
       */
      className="m-auto max-w-[min(28rem,calc(100vw-2rem))] rounded-lg border border-rule bg-surface p-0 text-ink shadow-float backdrop:bg-black/50 backdrop:backdrop-blur-[2px]"
      onMouseDown={(event) => {
        pressedBackdrop.current = event.target === ref.current;
      }}
      onClick={(event) => {
        // Both ends of the gesture must be on the backdrop — see `pressedBackdrop`.
        if (event.target === ref.current && pressedBackdrop.current) onClose();
        pressedBackdrop.current = false;
      }}
    >
      <header className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <h2 className="text-sm font-medium">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="qs-icon-button rounded px-1.5 text-ink-muted hover:text-ink"
          aria-label="Close"
        >
          ✕
        </button>
      </header>
      <div className="p-4">{children}</div>
    </dialog>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string | undefined;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-3">
      <label htmlFor={htmlFor} className="qs-eyebrow mb-1 block">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-2xs text-ink-faint">{hint}</p>}
    </div>
  );
}

export const inputClass =
  'w-full rounded border border-rule bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent';
